import type { AxiosResponse, AxiosRequestConfig } from 'axios';

import axios from 'axios';
import i18next from 'i18next';

import { CONFIG } from 'src/global-config';
import { buildSupportMailtoUrl } from 'src/lib/buildSupportMailToUrl';

import { toast } from 'src/components/snackbar';

const JWT_STORAGE_KEY = 'jwt_access_token';

export type PaginatedResponse<T> = {
  data: T;
  totalCount: number;
};

export const updateJWTFromResponseHeaders = (headers: AxiosResponse['headers']): void => {
  const newToken = headers?.authorization;
  if (newToken && newToken.startsWith('Bearer ')) {
    const token = newToken.replace('Bearer ', '');
    localStorage.setItem(JWT_STORAGE_KEY, token);
    axiosInstance.defaults.headers.common.Authorization = newToken;
  }
};

export const getBearerToken = () => localStorage.getItem(JWT_STORAGE_KEY);

const axiosInstance = axios.create({
  baseURL: CONFIG.serverUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use((config) => {
  const token = getBearerToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    updateJWTFromResponseHeaders(response.headers);
    return response;
  },
  (error) => {
    const response = error?.response;
    const message = response?.data?.message || error?.message || 'Something went wrong!';

    console.error('Axios error:', message);

    if (response?.status === 500) {
      toast.error(i18next.t('errors.serverError.title', { ns: 'common' }), {
        description: i18next.t('errors.serverError.description', { ns: 'common' }),
        duration: 10000,
        action: {
          label: i18next.t('errors.serverError.contactSupport', { ns: 'common' }),
          onClick: () => {
            window.open(buildSupportMailtoUrl(), '_self');
          },
        },
      });
    }

    // Preserve the original error object with response data for field validation
    if (response) {
      error.message = message;
      error.fieldErrors = response.data.fieldErrors || [];
      return Promise.reject(error);
    }

    return Promise.reject(new Error(message));
  }
);

export default axiosInstance;

export const fetcher = async <T = unknown>(
  args: string | [string, AxiosRequestConfig]
): Promise<T> => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args, {}];

    const res = await axiosInstance.get<T>(url, config);

    return res.data;
  } catch (error) {
    console.error('Fetcher failed:', error);
    throw error;
  }
};

export const fetcherWithHeaders = async <T = unknown>(
  url: string
): Promise<PaginatedResponse<T>> => {
  try {
    const res = await axiosInstance.get<T>(url);
    const totalCountHeader = res.headers?.['x-total-count'];
    const totalCount = typeof totalCountHeader === 'string' ? parseInt(totalCountHeader, 10) : 0;

    return { data: res.data, totalCount };
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

export const endpoints = {
  auth: {
    validate: '/api/auth',
    me: '/api/account',
    signIn: '/api/authenticate',
    signUp: '/api/auth/sign-up',
  },
  me: {
    assumedRole: {
      set: (roleId: number) => `/api/me/assumed-role/${roleId}`,
      permissions: '/api/me/assumed-role/permissions',
    },
    adminRoles: '/api/me/admin-roles',
    invitations: '/api/me/invitations',
    acceptInvitation: (invitationId: number) => `/api/me/invitations/${invitationId}/accept`,
    denyInvitation: (invitationId: number) => `/api/me/invitations/${invitationId}/deny`,
  },
  admin: {
    management: {
      users: (mainGroupId: number) => `/api/admin/management/group/${mainGroupId}/users`,
      removeUser: (mainGroupId: number, userId: number) =>
        `/api/admin/management/group/${mainGroupId}/users/${userId}`,
      invitations: (mainGroupId: number) =>
        `/api/admin/management/group/${mainGroupId}/users/invitations`,
      groupsTreeData: (adminRoleId: number) => `/api/admin/roles/${adminRoleId}/groups`,
      professionalGroups: (adminRoleId: number) =>
        `/api/admin/roles/${adminRoleId}/professional-groups`,
      managedAdminRoles: (adminRoleId: number) =>
        `/api/admin/roles/${adminRoleId}/managed-admin-roles`,
      removeInvitation(mainGroupId: number, invitationId: number) {
        return `/api/admin/management/group/${mainGroupId}/users/invitations/${invitationId}`;
      },
      userDetail: (mainGroupId: number, userId: number) =>
        `/api/admin/management/group/${mainGroupId}/users/${userId}`,
      updateUserGeneralData: (mainGroupId: number, userId: number) =>
        `/api/admin/management/group/${mainGroupId}/users/${userId}/general-data`,
      updateCreditSettings: (mainGroupId: number, userId: number) =>
        `/api/admin/management/group/${mainGroupId}/users/${userId}/credit-settings`,
      bookCredits: (mainGroupId: number, userId: number) =>
        `/api/admin/management/group/${mainGroupId}/users/${userId}/credits`,
      updateUserGroups: (mainGroupId: number, userId: number) =>
        `/api/admin/management/group/${mainGroupId}/users/${userId}/groups`,
      updateUserAdminRoles: (mainGroupId: number, userId: number) =>
        `/api/admin/management/group/${mainGroupId}/users/${userId}/admin-roles`,
      setUserPassword: (mainGroupId: number, userId: number) =>
        `/api/admin/management/group/${mainGroupId}/users/${userId}/password`,
      groups: (mainGroupId: number) => `/api/admin/management/group/${mainGroupId}/groups`,
      updateGroup: (mainGroupId: number, groupId: number) =>
        `/api/admin/management/group/${mainGroupId}/groups/${groupId}`,
    },
    emails: {
      send: '/api/admin/emails/send',
    },
  },
  superAdmin: {
    management: {
      users: '/api/superadmin/management/users',
      userStatistics: '/api/superadmin/management/users/statistics',
      getUserGroupIds: (userId: number) => `/api/superadmin/management/users/${userId}/group-ids`,
      editUserGroups: (userId: number) => `/api/superadmin/management/users/${userId}/groups`,
      bulkBookCredits: '/api/superadmin/management/users/bulk/book-credits',
      bulkTargetHours: '/api/superadmin/management/users/bulk/target-hours',
      setUserPassword: (userId: number) => `/api/superadmin/management/users/${userId}/password`,
      groups: '/api/superadmin/management/groups',
      groupsTreeData: '/api/superadmin/management/groups/tree-data',
      addUsersToGroup: (groupId: number) => `/api/superadmin/management/groups/${groupId}/users`,
      viewUserGroupHistory: (userId: number) =>
        `/api/superadmin/management/users/${userId}/grouphistories`,
    },
  },
  examination: {
    details: (id: number) => `/api/examinations/${id}`,
    start: (id: number) => `/api/examinations/${id}/attempts`,
    submitAnswer: (attemptId: number) => `/api/examination-attempts/${attemptId}/answers`,
    abort: (attemptId: number) => `/api/examination-attempts/${attemptId}/abort`,
    statisticsByLessonAndTodo: (lessonId: number, todoId: number) =>
      `/api/statistics/lessons/${lessonId}/question-statistics?mandatory-unit-id=${todoId}`,
    userStatisticsByLessonAndTodo: (lessonId: number, todoId: number) =>
      `/api/statistics/lessons/${lessonId}/user-statistics?mandatory-unit-id=${todoId}`,
    attemptDetails: (attemptId: number, todoId: number) =>
      `/api/examination-attempts/${attemptId}/details?mandatory-unit-id=${todoId}`,
    userCertificateStatus: (mandatoryUnitId: number) =>
      `/api/statistics/mandatory-units/${mandatoryUnitId}/user-certificate-status`,
  },
  qrCode: {
    lesson: (lessonId: number) => `/api/lessons/${lessonId}/qr-code`,
    resolve: (code: string) => `/api/qr-codes/${code}`,
  },
  appUpdates: {
    recent: '/api/app-updates/recent',
    seen: (id: number) => `/api/app-updates/${id}/seen`,
    like: (id: number) => `/api/app-updates/${id}/like`,
  },
} as const;
